import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { router } from './routes';
import { onAuthStateChanged, updateProfile as firebaseUpdateProfile } from 'firebase/auth';
import { auth } from './lib/firebase';
import { useAuthStore } from './lib/stores/authStore';
import { usePmBalanceStore } from './lib/stores/pmBalanceStore';
import { identifyUser, clearUser } from './lib/analytics';
import { trackPageView } from './lib/page-view-tracking';
import { notificationService } from './lib/notifications';
import { startVersionCheck } from './lib/versionCheck';
// Soundtrack is now server-driven: lobby + game WS messages
// (`soundtrack_play` / `fx_play`) are dispatched directly into the
// engine from the store handlers via `audio-routing` — no provider
// component needed.
import { initAudioRouting } from './lib/audio/audio-routing';
import { REFERRAL_CODE_STORAGE_KEY } from './lib/auth-service';

// Inactivity is now handled per-layout: LobbyLayout (10min → /dashboard) and
// GameLayout (3min → /lobby). The previous global hook was removed because it
// signed users out on idle, violating Inv 3 ("auth expiry is not logout").

function App() {
  const { setUser, setLoading, clearSignedOutElsewhere, updateProfile } = useAuthStore();

  useEffect(() => {
    startVersionCheck();
  }, []);

  // Capture a friend's referral code from `?ref=CODE` into sessionStorage so it
  // survives through guest sign-in and any later signup/conversion redirect.
  // Redemption itself only happens at that later transition (see
  // `auth-service.ts`'s `redeemPersistedReferralCode`), never here.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref?.trim()) {
      sessionStorage.setItem(REFERRAL_CODE_STORAGE_KEY, ref.trim().toUpperCase());
    }
  }, []);

  // Router-level page_view tracking (with page_title) on every navigation,
  // createBrowserRouter has no built-in analytics, so we subscribe to the data
  // router. Fire once for the initial load, then on each location change.
  useEffect(() => {
    trackPageView(window.location.pathname);
    return router.subscribe((state) => trackPageView(state.location.pathname));
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        clearUser();
        setUser(null);
        setLoading(false);
        return;
      }

      clearSignedOutElsewhere();

      // GA4 identity + core user properties (cross-platform stitch = Firebase
      // UID). is_paying derives downstream from purchase events; chars_created
      // derives from character_create_complete, which web does not fire yet
      // (mobile-only today), so it lands via the stitched mobile event.
      // highest_level is a known v1 gap.
      const signupDate = user.metadata.creationTime
        ? new Date(user.metadata.creationTime).toISOString().slice(0, 10)
        : null;
      identifyUser(
        user.uid,
        signupDate ? { platform: 'web', signup_date: signupDate } : { platform: 'web' }
      );

      // Hydrate the backend profile (passkey status + PM balance). For a GUEST
      // (passkey status + PM balance). Guests are provisioned EXPLICITLY by
      // `startGuest` via POST /api/guest/start (Turnstile-gated), so GET
      // /api/users/me is READ-ONLY here. For a returning guest it returns the
      // existing profile; for a brand-new guest mid-`startGuest` it 404s (null),
      // and we defer `setUser` to `startGuest` so we never flip `isLoggedIn` (and
      // route to the lobby) before the gift exists. First-seen Google users ARE
      // provisioned by this read (see `ensure_profile`).
      const API_BASE = import.meta.env.VITE_API_URL || '';
      const hydrateProfile = async (): Promise<{ displayName?: string } | null> => {
        try {
          const token = await user.getIdToken();
          const res = await fetch(`${API_BASE}/api/users/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const profile = res.ok ? await res.json() : null;
          if (!profile) return null;
          if (profile.passkeyRegistered != null) {
            updateProfile({ passkeyRegistered: profile.passkeyRegistered });
          }
          if (typeof profile.pmTokenBalance === 'number') {
            usePmBalanceStore.getState().setPmBalance(profile.pmTokenBalance);
          }
          // Mirror the backend-assigned canonical username (a collision may have
          // turned 'avive' into 'avive2') into Firebase Auth + refresh the token
          // so the lobby (token `name` claim) shows the same handle. No-op when
          // they match. Guests only — full accounts manage their own name.
          if (user.isAnonymous && profile.displayName && profile.displayName !== user.displayName) {
            await firebaseUpdateProfile(user, { displayName: profile.displayName });
            await user.getIdToken(true);
          }
          return profile;
        } catch {
          // Non-fatal — the next read self-heals.
          return null;
        }
      };

      if (user.isAnonymous) {
        const profile = await hydrateProfile();
        if (!profile) {
          // Brand-new guest mid-`startGuest`: it owns provisioning + `setUser`.
          // Don't flip isLoggedIn yet (no lobby route before the gift exists).
          setLoading(false);
          return;
        }
        setUser(user);
      } else {
        setUser(user);
        void hydrateProfile();
      }

      // Request notification permission on first login (per spec lines 1390-1393)
      if (Notification.permission === 'default') {
        // Small delay to not interrupt login flow
        setTimeout(() => {
          notificationService
            .requestPermission()
            .then(() => {
              // Notification permission handled
            })
            .catch((error) => {
              console.error('Failed to request notification permission:', error);
            });
        }, 2000);
      }

      // Note: Auto-reconnect to active session was removed because the REST endpoint
      // /api/game/active does not exist. Users can navigate to game sessions manually
      // via URL or through the lobby interface.
    });

    return () => unsubscribe();
  }, [setUser, setLoading, clearSignedOutElsewhere, updateProfile]);

  // Boot audio routing once at app mount — wires the singleton
  // SoundtrackEngine to server-pushed audio WS messages.
  useEffect(() => {
    initAudioRouting();
  }, []);

  return <RouterProvider router={router} />;
}

export default App;
